import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Spacer,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { CSVLink } from 'react-csv';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { FundingRequest, Util } from '@finley-technologies/finley-types';

import { ReceivableBorrowingBaseTestWaiverGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  BorrowerRecentWaiverUpdatesAggregate,
  EligibilityWaiverImpactForKey,
  generateTotalEligibilityWaiverImpactAndCsvDataRows,
  getBorrowerRecentWaiverUpdatesAggregateByTest,
} from './utils';
import { VerificationBorrowerRecentWaiverUpdateIcon } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

const waiverImpactTableDataKeyToHeader = [
  { key: 'externalReceivableId', label: 'External Receivable ID' },
  { key: 'borrowingBaseTestType', label: 'Waiver Type' },
  { key: 'borrowingBaseTestKey', label: 'Test' },
  { key: 'eligibilityKickoutWaived', label: 'Eligibility Kickout Waived' },
];

type WaiverAggregateAccordionButtonRowProps = {
  isExpanded: boolean;
  templateColumns: string;
  eligibilityCriteriaTestKey: string;
  totalWaiverImpactForTestKey: number;
  numberOfWaiversForTestKey: number;
  numberOfBorrowerRecentWaiverUpdates: number;
};
const WaiverAggregateAccordionButtonRow = ({
  isExpanded,
  templateColumns,
  eligibilityCriteriaTestKey,
  totalWaiverImpactForTestKey,
  numberOfWaiversForTestKey,
  numberOfBorrowerRecentWaiverUpdates,
}: WaiverAggregateAccordionButtonRowProps) => (
  <Box padding="8px 16px" height="100%" width="100%">
    <Grid minH="48px" templateColumns={templateColumns} gap="16px">
      <GridItem
        alignSelf="center"
        fontSize="16px"
        fontWeight={600}
        textAlign="left"
        whiteSpace="pre-line"
        verticalAlign="text-bottom"
        display="flex"
        alignItems="center"
      >
        <Text pr="32px">
          {commonUtils.toTitleCase(eligibilityCriteriaTestKey)}
        </Text>
      </GridItem>

      <GridItem alignSelf="center">
        <Text
          textAlign="left"
          fontWeight={500}
        >{`Total Waivers: ${numberOfWaiversForTestKey}`}</Text>
        <Flex alignItems="center">
          {Boolean(numberOfBorrowerRecentWaiverUpdates > 0) && (
            <VerificationBorrowerRecentWaiverUpdateIcon />
          )}
          <Text
            textAlign="left"
            fontWeight={400}
            fontSize="14px"
          >{`Borrower Recent Updates: ${numberOfBorrowerRecentWaiverUpdates}`}</Text>
        </Flex>
      </GridItem>

      <GridItem
        alignSelf="center"
        padding="8px 16px"
        backgroundColor={colors.aurora[50]}
      >
        <Flex>
          <Text fontSize="16px" fontWeight={600} textAlign="left">
            {commonUtils.numberToCurrencyWithSymbol({
              number: totalWaiverImpactForTestKey,
            })}
          </Text>
          <Spacer />
          <Icon
            icon={isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'}
            size="large"
            color={colors.aurora[400]}
          />
        </Flex>
      </GridItem>
    </Grid>
  </Box>
);

type WaiverAggregateAccordionPanelRowProps = {
  templateColumns: string;
  externalReceivableId: string;
  waiverImpactForReceivable: number;
  borrowerRecentWaiverUpdatesForKey?: ReceivableBorrowingBaseTestWaiverGQLType;
};
const WaiverAggregateAccordionPanelRow = ({
  templateColumns,
  externalReceivableId,
  waiverImpactForReceivable,
  borrowerRecentWaiverUpdatesForKey,
}: WaiverAggregateAccordionPanelRowProps) => {
  const tooltipLabelForReceivableId = borrowerRecentWaiverUpdatesForKey
    ? `Last Updated by ${
        borrowerRecentWaiverUpdatesForKey.lastUpdatedOrganizationName
      }, on ${commonDate.epochToSlashedDateStr(
        borrowerRecentWaiverUpdatesForKey.updated_at,
      )}`
    : '';

  return (
    <Box backgroundColor={colors.background[50]} borderRadius="8px" mt="8px">
      <Grid minH="48px" templateColumns={templateColumns} gap="16px">
        {/* leave room for eligibility criteria test key column already displayed by main accordion row */}
        <GridItem alignSelf="center" />

        <GridItem alignSelf="center">
          <Tooltip label={tooltipLabelForReceivableId} placement="bottom-start">
            <Flex alignItems="center">
              {borrowerRecentWaiverUpdatesForKey && (
                <VerificationBorrowerRecentWaiverUpdateIcon />
              )}
              <Text>{externalReceivableId}</Text>
            </Flex>
          </Tooltip>
        </GridItem>

        <GridItem
          alignSelf="center"
          padding="8px 16px"
          backgroundColor={colors.gray[20]}
        >
          <Text fontSize="16px" fontWeight={600}>
            {commonUtils.numberToCurrencyWithSymbol({
              number: waiverImpactForReceivable,
            })}
          </Text>
        </GridItem>
      </Grid>
    </Box>
  );
};

type WaiverImpactEligibilityAggregateAccordionProps = {
  eligibilityTestKey: string;
  eligibilityWaiverImpactForKey: EligibilityWaiverImpactForKey;
  templateColumns: string;
  borrowerRecentWaiverUpdatesAggregateForKey?: BorrowerRecentWaiverUpdatesAggregate;
};
const WaiverImpactEligibilityAggregateAccordion = ({
  eligibilityTestKey,
  eligibilityWaiverImpactForKey,
  templateColumns,
  borrowerRecentWaiverUpdatesAggregateForKey,
}: WaiverImpactEligibilityAggregateAccordionProps) => {
  const numberOfBorrowerRecentWaiverUpdatesWithImpact = _.filter(
    eligibilityWaiverImpactForKey.waiverImpacts,
    (waiverImpact) =>
      Boolean(
        borrowerRecentWaiverUpdatesAggregateForKey?.[
          waiverImpact.finleyReceivableId
        ],
      ),
  );
  return (
    <>
      <Accordion allowMultiple mb="16px">
        <AccordionItem
          backgroundColor={colors.background[50]}
          borderRadius="8px"
          border={`1px solid ${colors.gray[200]}`}
        >
          {({ isExpanded }) => (
            <>
              <AccordionButton
                paddingInline="0"
                paddingBlock="0"
                _disabled={{ color: 'black', cursor: 'default' }}
              >
                {WaiverAggregateAccordionButtonRow({
                  isExpanded,
                  templateColumns,
                  eligibilityCriteriaTestKey: eligibilityTestKey,
                  totalWaiverImpactForTestKey:
                    eligibilityWaiverImpactForKey.totalWaiverImpact,
                  numberOfWaiversForTestKey:
                    eligibilityWaiverImpactForKey.waiverImpacts.length,
                  numberOfBorrowerRecentWaiverUpdates:
                    numberOfBorrowerRecentWaiverUpdatesWithImpact.length ?? 0,
                })}
              </AccordionButton>

              <AccordionPanel>
                {_.map(
                  eligibilityWaiverImpactForKey.waiverImpacts,
                  (waiverImpactForKey) => (
                    <WaiverAggregateAccordionPanelRow
                      templateColumns={templateColumns}
                      externalReceivableId={
                        waiverImpactForKey.externalReceivableId
                      }
                      waiverImpactForReceivable={
                        waiverImpactForKey.eligibilityKickoutWaived ?? 0
                      }
                      borrowerRecentWaiverUpdatesForKey={
                        borrowerRecentWaiverUpdatesAggregateForKey?.[
                          waiverImpactForKey.finleyReceivableId
                        ]
                      }
                      key={`${eligibilityTestKey}-${waiverImpactForKey.finleyReceivableId}`}
                    />
                  ),
                )}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </>
  );
};

type VerificationReportWaiverImpactEligibilityProps = {
  eligibilityWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  receivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[];
  reportingDate: Util.FullDateStr;
  borrowerOrganizationName: string;
};
export const VerificationReportWaiverImpactEligibility = ({
  eligibilityWaiverImpact,
  receivableWaivers,
  reportingDate,
  borrowerOrganizationName,
}: VerificationReportWaiverImpactEligibilityProps) => {
  const totalEligibilityWaiverImpactAndCsvRows = generateTotalEligibilityWaiverImpactAndCsvDataRows(
    {
      eligibilityWaiverImpact,
    },
  );

  const borrowerRecentWaiverUpdatesAggregateByTest = getBorrowerRecentWaiverUpdatesAggregateByTest(
    { receivableWaivers, borrowerOrganizationName, reportingDate },
  );

  const filename = `eligibility-waiver-impact-${commonDate.currentDateStr()}.csv`;
  const templateColumns = 'repeat(3, 1fr)';

  return (
    <>
      <Flex verticalAlign="middle">
        <Text fontWeight={500} mb="24px">{`Total Receivables Waived: ${_.size(
          eligibilityWaiverImpact,
        )}`}</Text>
        <Spacer />
        {/* !TODO @eldarshamukhmedov react-csv is no longer maintained, needs to be replaced */}
        <CSVLink
          data={totalEligibilityWaiverImpactAndCsvRows.csvDataRows}
          headers={waiverImpactTableDataKeyToHeader}
          filename={filename}
        >
          <Flex alignItems="baseline">
            <Text color={colors.blue[400]} fontSize="14px" mr="10px">
              Download CSV
            </Text>
            <Icon icon="arrow-down-to-bracket" color={colors.blue[400]} />
          </Flex>
        </CSVLink>
      </Flex>
      {_.map(
        totalEligibilityWaiverImpactAndCsvRows.eligibilityWaiverImpactByTestKey,
        (eligibilityWaiverImpactForKey, eligibilityTestKey) => (
          <WaiverImpactEligibilityAggregateAccordion
            eligibilityTestKey={eligibilityTestKey}
            eligibilityWaiverImpactForKey={eligibilityWaiverImpactForKey}
            templateColumns={templateColumns}
            borrowerRecentWaiverUpdatesAggregateForKey={
              borrowerRecentWaiverUpdatesAggregateByTest
                ? borrowerRecentWaiverUpdatesAggregateByTest[eligibilityTestKey]
                : undefined
            }
            key={eligibilityTestKey}
          />
        ),
      )}

      {/* Total Eligibility Waiver Impact Bottom Line */}
      <Divider />
      <Box
        padding="8px 16px"
        height="100%"
        width="100%"
        backgroundColor={colors.background[50]}
        borderRadius="8px"
        border={`1px solid ${colors.gray[200]}`}
        mt="32px"
      >
        <Grid minH="48px" templateColumns={templateColumns} gap="16px">
          <GridItem
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            textAlign="left"
            whiteSpace="pre-line"
            verticalAlign="text-bottom"
            display="flex"
            alignItems="center"
          >
            <Text>Total Waived</Text>
          </GridItem>

          {/* receivableId column for expanded rows */}
          <GridItem alignSelf="center" />

          <GridItem
            alignSelf="center"
            padding="8px 16px"
            backgroundColor={colors.aurora[50]}
          >
            <Flex>
              <Text fontSize="16px" fontWeight={600} textAlign="left">
                {commonUtils.numberToCurrencyWithSymbol({
                  number:
                    totalEligibilityWaiverImpactAndCsvRows.totalEligibilityWaiverImpact,
                })}
              </Text>
            </Flex>
          </GridItem>
        </Grid>
      </Box>
    </>
  );
};
